<script setup lang="ts">
import { computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { isRecord, toNullableBoolean, toNullableNumber, toNullableString } from '@/utils/coerce';
import { getRiskLevelDisplay } from '@/utils/kyc';
import type { KycCategory } from '@/types/kyc';

interface LocationCountryDataJson {
  country: string | null;
  isMainForEntity: boolean;
  fatfClassification: string | null;
  levelValue: number | null;
  ruleId: string | null;
}

interface Props {
  category: KycCategory;
}

const props = defineProps<Props>();

const unwrapRecord = (item: Record<string, unknown>): Record<string, unknown> => {
  if (isRecord(item._custom) && isRecord(item._custom.value)) {
    return item._custom.value;
  }
  return item;
};

const normalizeLocation = (item: Record<string, unknown>): LocationCountryDataJson => ({
  country: toNullableString(item.country),
  isMainForEntity: toNullableBoolean(item.is_main_for_entity) ?? false,
  fatfClassification: toNullableString(item.fatf_classification),
  levelValue: toNullableNumber(item.level_value),
  ruleId: toNullableString(item.rule_id),
});

const locations = computed<LocationCountryDataJson[]>(() => {
  const raw = props.category.data_json;
  const items = Array.isArray(raw)
    ? raw
    : isRecord(raw)
      ? raw.locations ?? raw.items
      : [];

  if (!Array.isArray(items)) return [];

  return items
    .filter(isRecord)
    .map(unwrapRecord)
    .filter(isRecord)
    .map(normalizeLocation)
    .sort((left, right) => {
      if (left.isMainForEntity !== right.isMainForEntity) return left.isMainForEntity ? -1 : 1;
      return (right.levelValue ?? -1) - (left.levelValue ?? -1);
    });
});

const mainLocation = computed(() => locations.value.find((location) => location.isMainForEntity) ?? locations.value[0] ?? null);

const secondaryLocations = computed(() => {
  if (!mainLocation.value) return [];
  return locations.value.filter((location) => location !== mainLocation.value);
});

const additionalLocationsCount = computed(() => {
  if (!mainLocation.value) return 0;
  return secondaryLocations.value.length;
});

const formatFatfLabel = (value: string | null): string => {
  if (!value) return 'Sem classificação FATF';
  if (value === 'grey_list') return 'Lista cinza FATF';
  if (value === 'black_list') return 'Lista negra FATF';
  if (value === 'black_list_or_grey_list') return 'Lista cinza ou negra FATF';
  return value;
};

const getFatfSeverity = (value: string | null): 'danger' | 'warning' | 'secondary' => {
  if (value === 'black_list') return 'danger';
  if (value === 'grey_list' || value === 'black_list_or_grey_list') return 'warning';
  return 'secondary';
};

const getRiskTagAttrs = (levelValue: number | null) => {
  const display = getRiskLevelDisplay(levelValue);
  return {
    value: display.label,
    style: {
      backgroundColor: display.hexBgColor,
      color: display.hexColor,
    },
    class: [display.borderClass, 'border'],
  };
};

</script>

<template>
  <div class="mt-3 space-y-4">
    <div
      v-if="mainLocation"
      class="rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 via-white to-white p-4 shadow-sm"
    >
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="flex items-start gap-3">
          <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
            <BaseIcon class="text-2xl">public</BaseIcon>
          </div>
          <div>
            <div class="text-[11px] font-semibold uppercase tracking-wide text-sky-700">
              País principal
            </div>
            <div class="mt-1 text-lg font-semibold text-slate-900">
              {{ mainLocation.country || 'País não informado' }}
            </div>
            <div class="mt-2 flex flex-wrap gap-2">
              <Tag severity="info" value="Principal" />
              <Tag :value="formatFatfLabel(mainLocation.fatfClassification)" :severity="getFatfSeverity(mainLocation.fatfClassification)" />
              <Tag v-bind="getRiskTagAttrs(mainLocation.levelValue)" />
            </div>
          </div>
        </div>

        <div
          v-if="additionalLocationsCount > 0"
          class="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm"
        >
          +{{ additionalLocationsCount }}
          {{ additionalLocationsCount === 1 ? 'localização adicional' : 'localizações adicionais' }}
        </div>
      </div>
    </div>

    <div
      v-if="secondaryLocations.length > 0"
      class="max-h-[min(24vh,36rem)] overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200 bg-white overscroll-y-contain"
    >
      <DataTable
        :value="secondaryLocations"
        class="p-datatable-sm text-sm"
        responsiveLayout="scroll"
        stripedRows
      >
        <Column header="Risco">
          <template #body="{ data }: { data: LocationCountryDataJson }">
            <Tag v-bind="getRiskTagAttrs(data.levelValue)" />
          </template>
        </Column>

        <Column header="País">
          <template #body="{ data }: { data: LocationCountryDataJson }">
            <div class="font-medium text-slate-900">{{ data.country || 'País não informado' }}</div>
            <div v-if="data.ruleId" class="text-[11px] text-slate-500">
              Regra <span class="font-mono">{{ data.ruleId.slice(0, 8) }}</span>
            </div>
          </template>
        </Column>

        <Column header="Perfil">
          <template #body="{ data }: { data: LocationCountryDataJson }">
            <Tag :value="data.isMainForEntity ? 'Principal' : 'Adicional'" :severity="data.isMainForEntity ? 'info' : 'secondary'" />
          </template>
        </Column>

        <Column header="Classificação FATF">
          <template #body="{ data }: { data: LocationCountryDataJson }">
            <Tag :value="formatFatfLabel(data.fatfClassification)" :severity="getFatfSeverity(data.fatfClassification)" />
          </template>
        </Column>
      </DataTable>
    </div>

    <div v-else-if="!locations.length" class="text-sm italic text-slate-500">
      Nenhum dado de localização por país encontrado.
    </div>
  </div>
</template>
